/**
 * «Активность» → «По дням»: хватает ли людей в смене. На каждый день — входящая нагрузка, как её
 * обработали, сколько было людей и часов, занятость по действиям; клик по дню — люди смены и
 * покрытие линии по часам. Спека `docs/specs/2026-10-26-activity-days-design.md`; числа — те же,
 * что у `prokat-runner activity-days`.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Fragment, type ReactNode, useState } from "react";
import { Link } from "react-router";

import { type Active, type DayPerson, type DayRow, type DaysReport, loadActivityDays } from "@/lib/activity";
import { workTime } from "@/lib/leads";

const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

function dayTitle(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${WEEKDAYS[d.getDay()]}`;
}

const pct = (n: number, d: number) => (d > 0 ? Math.round((n * 100) / d) : null);
const dash = (v: number | null | undefined, suffix = "") => (v === null || v === undefined ? "—" : `${v}${suffix}`);

/** Секунды ожидания по-человечески: 95 → «1,6 мин», 4000 → «67 мин». */
function waitMin(sec: number | null | undefined): string {
  if (sec === null || sec === undefined) return "—";
  const m = sec / 60;
  return m < 10 ? `${m.toFixed(1).replace(".", ",")} мин` : `${Math.round(m)} мин`;
}

/** Минуты перезвона: до часа — минутами, дальше — часами. */
function cbMin(m: number | null | undefined): string {
  if (m === null || m === undefined) return "—";
  return m < 60 ? `${Math.round(m)} мин` : `${(m / 60).toFixed(1).replace(".", ",")} ч`;
}

/** Плохое — рыжим: порог смотрим по смыслу колонки. */
function Warn({ bad, children }: { bad: boolean; children: ReactNode }) {
  return <span className={bad ? "text-warn" : ""}>{children}</span>;
}

export function Tile({ label, value, hint, extra }: { label: string; value: ReactNode; hint?: string; extra?: ReactNode }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
      {extra && <div className="text-[12px] text-fg-faint">{extra}</div>}
    </div>
  );
}

/** Полоса занятости: входящие · исходящие · чаты · группы · панель — доли оплачиваемого часа. */
function BusyBar({ a, paidMin }: { a: Active; paidMin: number }) {
  const total = paidMin * 60;
  if (total <= 0) return null;
  const parts: [string, number, string][] = [
    ["входящие", a.in_sec, "bg-accent"],
    ["исходящие", a.out_sec, "bg-warn"],
    ["чаты", a.chat_sec, "bg-ok"],
    ["группы", a.group_sec, "bg-fg-faint"],
    ["панель расширения", a.panel_sec, "bg-border-strong"],
  ];
  return (
    <div
      className="flex h-2 w-28 overflow-hidden rounded-full bg-bg"
      title={parts.map(([l, s]) => `${l} ${Math.round((s * 100) / total)}%`).join(" · ")}
    >
      {parts.map(([l, s, c]) => (
        <div key={l} className={c} style={{ width: `${(s * 100) / total}%` }} />
      ))}
    </div>
  );
}

type Col = { key: string; group: string; head: string; hint: string; cell: (d: DayRow) => ReactNode };

const COLS: Col[] = [
  {
    key: "people",
    group: "Люди",
    head: "чел.",
    hint: "сотрудники «Контакт Центра» в оплачиваемых статусах за день",
    cell: (d) => d.staff.people,
  },
  {
    key: "paid",
    group: "Люди",
    head: "часы",
    hint: "оплачиваемое время по учёту CRM, сумма по людям",
    cell: (d) => workTime(d.staff.paid_min),
  },
  {
    key: "tail",
    group: "Люди",
    head: "без действий в конце",
    hint: "оплачиваемое время ПОСЛЕ последнего действия сотрудника (звонок, сообщение, панель) — статус «на линии» не сняли, а человека уже нет",
    cell: (d) => (
      <Warn bad={d.staff.after_last_min >= 60}>{d.staff.after_last_min >= 1 ? workTime(d.staff.after_last_min) : "—"}</Warn>
    ),
  },
  {
    key: "numbers",
    group: "Входящие звонки",
    head: "номеров",
    hint: "разных номеров, звонивших на линию за день (клиент набирает по 2–3 раза — считаем номер)",
    cell: (d) => d.calls.numbers,
  },
  {
    key: "answered",
    group: "Входящие звонки",
    head: "принято",
    hint: "доля номеров, чей звонок взяли сразу; меньше 80% — рыжим",
    cell: (d) => <Warn bad={(d.calls.answered_pct ?? 100) < 80}>{dash(d.calls.answered_pct, "%")}</Warn>,
  },
  {
    key: "missed",
    group: "Входящие звонки",
    head: "пропущено 10–21",
    hint: "пропущенные номера, когда кто-то был на линии, с 10 до 21",
    cell: (d) => <Warn bad={(d.calls.work?.numbers ?? 0) > 10}>{d.calls.work?.numbers ?? "—"}</Warn>,
  },
  {
    key: "cb",
    group: "Входящие звонки",
    head: "перезвон, медиана",
    hint: "сколько ждал пропущенный в 10–21 до разговора с нами (перезвонили или он дозвонился сам): медиана без резких выбросов (Тьюки 1,5·IQR); в скобках p90 по всем — долгий перезвон тоже потеря",
    cell: (d) =>
      d.calls.work ? (
        <span title={`без отсечения: медиана ${cbMin(d.calls.work.median_raw)}, выбросов ${d.calls.work.outliers}`}>
          <Warn bad={(d.calls.work.median ?? 0) > 20}>{cbMin(d.calls.work.median)}</Warn>
          <span className="text-fg-faint"> ({cbMin(d.calls.work.p90)})</span>
        </span>
      ) : (
        "—"
      ),
  },
  {
    key: "nocb",
    group: "Входящие звонки",
    head: "не перезвонили",
    hint: "пропущенные в 10–21, с которыми так и не поговорили (без невалидных номеров и «пытались 3 раза»)",
    cell: (d) => <Warn bad={(d.calls.work?.not_called_back ?? 0) > 0}>{d.calls.work?.not_called_back ?? "—"}</Warn>,
  },
  {
    key: "turns",
    group: "Чаты",
    head: "реплик клиентов",
    hint: "сколько раз клиент написал и ждал ответа: подряд идущие сообщения клиента — одна реплика; все каналы, кроме групп и автосообщений",
    cell: (d) => (
      <span title={`сообщений клиентов ${d.chats.in_msgs}, наших ${d.chats.out_msgs}, диалогов ${d.chats.dialogs}`}>{d.chats.turns}</span>
    ),
  },
  {
    key: "fast",
    group: "Чаты",
    head: "ответ ≤ 5 мин",
    hint: "доля реплик, на которые ответили за 5 минут рабочего времени (10–21); меньше 60% — рыжим",
    cell: (d) => {
      const v = pct(d.chats.turns_fast, d.chats.turns);
      return <Warn bad={(v ?? 100) < 60}>{dash(v, "%")}</Warn>;
    },
  },
  {
    key: "tmed",
    group: "Чаты",
    head: "ответ, медиана",
    hint: "медиана ожидания ответа на реплику, только рабочие часы; в скобках p90",
    cell: (d) => (
      <span>
        {waitMin(d.chats.turn_median_sec)}
        <span className="text-fg-faint"> ({waitMin(d.chats.turn_p90_sec)})</span>
      </span>
    ),
  },
  {
    key: "open",
    group: "Чаты",
    head: "висит к 21:00",
    hint: "реплики дня, на которые к 21:00 так и не ответили — очередь, которую смена не закрыла",
    cell: (d) => <Warn bad={d.chats.open_at_end > 20}>{d.chats.open_at_end}</Warn>,
  },
  {
    key: "contacts",
    group: "Чаты",
    head: "новых обращений",
    hint: "клиент написал после 72 часов тишины — как «Чаты · Аналитика»",
    cell: (d) => d.chats.contacts,
  },
  {
    key: "deal",
    group: "Исходящие",
    head: "по сделкам",
    hint: "исходящих по сделкам / из них разговоров по разметке расшифровки",
    cell: (d) => `${d.outbound.deal} / ${d.outbound.deal_talk}`,
  },
  {
    key: "lead",
    group: "Исходящие",
    head: "по лидам",
    hint: "исходящих по лидам / разговоров",
    cell: (d) => `${d.outbound.lead} / ${d.outbound.lead_talk}`,
  },
  {
    key: "busy",
    group: "Занятость",
    head: "всего",
    hint: "занятость по действиям: звонки и сообщения без двойного счёта / оплачиваемые часы (панель расширения не входит — она стоит не у всех)",
    cell: (d) => dash(d.load.pct, "%"),
  },
  {
    key: "must",
    group: "Занятость",
    head: "обязательная",
    hint: "входящие звонки + переписка с клиентами: то, что приходит само и не откладывается. По ней и решают, сколько людей нужно",
    cell: (d) => dash(d.load.must_pct, "%"),
  },
  {
    key: "outp",
    group: "Занятость",
    head: "исходящий обзвон",
    hint: "исходящие звонки: эластичная работа — растягивается на любое число людей",
    cell: (d) => dash(d.load.out_pct, "%"),
  },
];

const GROUPS = COLS.reduce<{ group: string; span: number }[]>((acc, c) => {
  const last = acc[acc.length - 1];
  if (last && last.group === c.group) last.span += 1;
  else acc.push({ group: c.group, span: 1 });
  return acc;
}, []);

const hhmm = (iso: string | null | undefined) => (iso ? iso.slice(11, 16) : "");

function PeopleOfDay({ d }: { d: DayRow }) {
  return (
    <table className="w-full text-[12px]" data-annot={`люди дня ${d.day}`}>
      <thead className="text-fg-muted">
        <tr>
          {["Сотрудник", "Смена", "Часы", "Последнее действие", "Занятость", "", "Старая", "Входящих", "Исходящих / разговоров", "Сообщений", "Групп", "Пропустил"].map((h, i) => (
            <th key={`${h}-${i}`} className="px-2 py-1 text-left font-normal">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {d.people.map((p: DayPerson) => {
          const tail = p.active.after_last_sec / 60;
          return (
            <tr key={p.user_id} className="border-t border-border">
              <td className="px-2 py-1">
                <Link to={`/activity/${p.user_id}?date=${d.day}`} className="text-accent hover:underline">
                  {p.name}
                </Link>
              </td>
              <td className="px-2 py-1 tabular-nums">
                {hhmm(p.shift_from)}–{hhmm(p.shift_to)}
              </td>
              <td className="px-2 py-1 tabular-nums">{workTime(p.paid_min)}</td>
              <td className="px-2 py-1 tabular-nums">
                {hhmm(p.active.last_at)}
                {tail >= 30 && <span className="ml-1 text-warn">статус ещё {workTime(tail)}</span>}
              </td>
              <td className="px-2 py-1 tabular-nums" title={`с панелью расширения ${dash(p.active.with_panel_pct, "%")}${p.active.ext ? "" : " · расширения нет"}`}>
                {dash(p.active.pct, "%")}
              </td>
              <td className="px-2 py-1">
                <BusyBar a={p.active} paidMin={p.paid_min} />
              </td>
              <td className="px-2 py-1 tabular-nums text-fg-faint" title="прежняя формула: разговор + дозвон + минута на каждый звонок, переписка не входит">
                {dash(p.busy_pct, "%")}
              </td>
              <td className="px-2 py-1 tabular-nums">{p.calls_in}</td>
              <td className="px-2 py-1 tabular-nums">
                {p.calls_out} / {p.dial_talk}
              </td>
              <td className="px-2 py-1 tabular-nums">{p.sent}</td>
              <td className="px-2 py-1 tabular-nums">{p.groups}</td>
              <td className="px-2 py-1 tabular-nums">{p.missed}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Покрытие по часам: сколько людей на линии против звонков и реплик клиентов в этот час. */
function Hours({ d }: { d: DayRow }) {
  const max = Math.max(1, ...d.hours.map((h) => h.calls + h.turns));
  return (
    <div className="flex flex-col gap-1" data-annot={`часы дня ${d.day}`}>
      <div className="text-[12px] text-fg-muted">
        По часам: людей на линии · входящих звонков · реплик клиентов в чатах
      </div>
      <div className="flex gap-1">
        {d.hours.map((h) => (
          <div key={h.hour} className="flex w-12 flex-col items-center gap-0.5 text-[11px] tabular-nums" title={`${h.hour}:00 — на линии ${h.people} чел., звонков ${h.calls}, реплик ${h.turns}`}>
            <div className="flex h-12 w-full items-end justify-center gap-0.5">
              <div className="w-2 bg-accent" style={{ height: `${(h.calls * 100) / max}%` }} />
              <div className="w-2 bg-ok" style={{ height: `${(h.turns * 100) / max}%` }} />
            </div>
            <div className={h.people < 1.5 && h.hour >= 10 && h.hour < 21 ? "font-medium text-warn" : "font-medium"}>{h.people}</div>
            <div className="text-fg-faint">
              {h.calls}·{h.turns}
            </div>
            <div className="text-fg-faint">{h.hour}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Totals({ r }: { r: DaysReport }) {
  const t = r.totals;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Tile label="Людей в смене" value={dash(t.people_avg)} hint={`в среднем за ${t.days} дн., часов ${workTime(t.paid_min)}`} />
      <Tile
        label="Звонков принято"
        value={dash(pct(t.calls_answered, t.calls_numbers), "%")}
        hint={`номеров ${t.calls_numbers}; перезвон пропущенным, медиана дней ${cbMin(t.callback_median)}`}
      />
      <Tile
        label="Чаты: ответ ≤ 5 мин"
        value={dash(pct(t.turns_fast, t.turns), "%")}
        hint={`реплик ${t.turns}, ответили ${pct(t.turns_answered, t.turns) ?? "—"}%; медиана дней ${waitMin(t.turn_median_sec)}`}
      />
      <Tile
        label="Занятость"
        value={dash(t.pct, "%")}
        hint={`обязательная (входящие + чаты) ${dash(t.must_pct, "%")}, исходящий обзвон ${dash(t.out_pct, "%")}`}
      />
      <Tile
        label="Статус без работы"
        value={workTime(t.after_last_min)}
        hint="оплачено после последнего действия сотрудника — забыли снять «на линии»"
      />
    </div>
  );
}

export function DaysView({ from, to }: { from: string; to: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const report = useQuery({
    queryKey: ["activity-days", from, to],
    queryFn: () => loadActivityDays({ from, to }),
    placeholderData: keepPreviousData,
  });
  if (report.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю по CRM и «Чатам»… две недели — около 10 секунд</div>;
  if (report.isError) return <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(report.error.message)}</div>;
  const r = report.data;
  return (
    <div className={`flex flex-col gap-4 ${report.isPlaceholderData ? "opacity-60" : ""}`}>
      <Totals r={r} />
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="по дням">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-muted">
              <tr>
                <th />
                {GROUPS.map((g) => (
                  <th key={g.group} colSpan={g.span} className="border-l border-border px-2.5 pt-2 text-center font-medium">
                    {g.group}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="px-4 py-2 text-left font-normal">День</th>
                {COLS.map((c, i) => (
                  <th
                    key={c.key}
                    title={c.hint}
                    className={`cursor-help whitespace-nowrap px-2.5 py-2 text-right font-normal ${i === 0 || COLS[i - 1]?.group !== c.group ? "border-l border-border" : ""}`}
                    data-annot={`колонка «${c.group}: ${c.head}»`}
                  >
                    {c.head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.days.map((d) => (
                <Fragment key={d.day}>
                  <tr
                    onClick={() => setOpen(open === d.day ? null : d.day)}
                    className={`cursor-pointer border-t border-border hover:bg-bg-hover ${d.staff.people <= 2 && d.staff.people > 0 ? "bg-bg" : ""}`}
                    data-annot={`день ${d.day}`}
                  >
                    <td className="whitespace-nowrap px-4 py-1.5">
                      {open === d.day ? "▾" : "▸"} {dayTitle(d.day)}
                    </td>
                    {COLS.map((c, i) => (
                      <td
                        key={c.key}
                        className={`whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums ${i === 0 || COLS[i - 1]?.group !== c.group ? "border-l border-border" : ""}`}
                      >
                        {c.cell(d)}
                      </td>
                    ))}
                  </tr>
                  {open === d.day && (
                    <tr className="border-t border-border bg-bg">
                      <td colSpan={COLS.length + 1} className="px-4 py-3">
                        <div className="flex flex-col gap-3">
                          <PeopleOfDay d={d} />
                          <Hours d={d} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div className="text-[12px] text-fg-faint">
        Наведите на заголовок колонки — там правило подсчёта; клик по дню — люди смены и покрытие по часам. Занятость
        считается по действиям: звонок (с минутой после разговора и 15 с после недозвона), 30 с на каждое отправленное
        сообщение; совпавшие по времени дела — один раз. Это допущения, не замер: чтение переписки и подбор платья без
        отправки в неё не попадают. Обязательная работа — входящие и переписка; исходящий обзвон заполняет любое
        свободное время, поэтому по нему людей не считают.
      </div>
    </div>
  );
}
